import React, { useMemo, useRef, useState } from 'react';
import { DesignElementLibraryItem, MAX_DESIGN_ELEMENTS_PER_OVERLAY } from '../../../api/tendApi';
import { useDesignElementsLibrary } from './useDesignElementsLibrary';

type Scope = 'universal' | 'brand';

interface DesignElementsPickerProps {
  brandId?: number | null;
  isSuperAdmin: boolean;
  canEditBrand: boolean;
  currentPlacementCount: number;
  onSelect: (item: DesignElementLibraryItem) => void;
  onClose: () => void;
}

const ALLOWED_ACCEPT = 'image/png,image/svg+xml';

export const DesignElementsPicker: React.FC<DesignElementsPickerProps> = ({
  brandId,
  isSuperAdmin,
  canEditBrand,
  currentPlacementCount,
  onSelect,
  onClose,
}) => {
  // Only superadmins see the Universal/Brand toggle (so they can choose
  // upload destination). Brand users see a single combined list (universal +
  // their brand's elements) and uploads always go to the brand collection.
  const showToggle = isSuperAdmin && brandId != null;
  const [scope, setScope] = useState<Scope>(isSuperAdmin ? 'universal' : 'brand');
  const { elements, loading, error, upload, remove } = useDesignElementsLibrary(brandId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!showToggle) {
      // Combined view for brand users (and superadmins without a brand).
      return elements;
    }
    if (scope === 'universal') return elements.filter((e) => e.is_universal);
    return elements.filter((e) => !e.is_universal && e.brand_id === brandId);
  }, [elements, scope, brandId, showToggle]);

  const atCap = currentPlacementCount >= MAX_DESIGN_ELEMENTS_PER_OVERLAY;
  // Upload destination when there's no toggle visible: brand users have a
  // brandId so they upload to brand; superadmins viewing without a brandId
  // (e.g. global template editor) upload to universal — defaulting to brand
  // would client-side error since there's no brandId to attach.
  const effectiveUploadScope: Scope = showToggle
    ? scope
    : (brandId != null ? 'brand' : 'universal');
  const canUploadInScope =
    effectiveUploadScope === 'universal' ? isSuperAdmin : canEditBrand;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      await upload({ file, scope: effectiveUploadScope, name: file.name });
    } catch (err) {
      setUploadError(String((err as Error).message || err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this design element? This cannot be undone.')) return;
    await remove(id);
  };

  const handleSelect = (item: DesignElementLibraryItem) => {
    if (atCap) return;
    onSelect(item);
  };

  return (
    <div
      className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm rounded-lg flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-2 border-b border-white/10">
        <h3 className="text-white text-sm font-medium">Design Elements</h3>

        {/* Universal/Brand toggle — only superadmins see this; brand users
            get a combined view and don't need to choose a scope. */}
        {showToggle && (
          <div className="flex items-center bg-black/40 rounded p-0.5 ml-2">
            <button
              type="button"
              onClick={() => setScope('universal')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                scope === 'universal' ? 'bg-yellow-600 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              Universal
            </button>
            <button
              type="button"
              onClick={() => setScope('brand')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                scope === 'brand' ? 'bg-blue-600 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              Brand
            </button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {canUploadInScope && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_ACCEPT}
                onChange={handleFile}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-2 py-1 rounded text-[11px] bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : '+ Upload'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white p-1"
            aria-label="Close design elements"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Cap warning */}
      {atCap && (
        <div className="px-3 py-1 bg-amber-600/40 text-amber-100 text-[11px]">
          Maximum {MAX_DESIGN_ELEMENTS_PER_OVERLAY} elements reached. Remove one to add more.
        </div>
      )}
      {(uploadError || error) && (
        <div className="px-3 py-1 bg-red-600/40 text-red-100 text-[11px]">{uploadError || error}</div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto p-3">
        {loading && <div className="text-white/60 text-[11px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-white/60 text-[11px]">
            {canUploadInScope ? 'No elements yet. Upload your first.' : 'No elements available.'}
          </div>
        )}
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {filtered.map((el) => (
            <div key={el.id} className="relative group">
              <button
                type="button"
                onClick={() => handleSelect(el)}
                disabled={atCap}
                className="w-full aspect-square bg-white/10 hover:bg-white/20 rounded p-2 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={el.name}
              >
                <img
                  src={el.s3_url}
                  alt={el.name}
                  className="max-w-full max-h-full object-contain pointer-events-none"
                  draggable={false}
                />
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); void handleDelete(el.id); }}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  aria-label={`Delete ${el.name}`}
                  title={`Delete ${el.name}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesignElementsPicker;
